/**
 * Quién ve qué contrato marco.
 *
 * # El principio
 *
 * Un contratista no puede ver los contratos de sus competidores. Es la misma
 * regla que ya rige la generación de actas, aplicada al módulo de contratos:
 * el alcance se calcula por PERTENENCIA, no por rol.
 *
 * # Los tres alcances
 *
 *   Personal Tecpetrol   todos los contratos (el filtro por disciplina que ya
 *                        existe sigue operando aguas arriba).
 *   ADOR / PLANNER       solo los contratos de SU empresa. En plural: un
 *                        contratista puede tener varios —Bobinados tiene dos,
 *                        eléctrica e instrumentación.
 *   INT                  los contratos de las OT que revisa. Un interventor
 *                        necesita el contrato del contratista al que fiscaliza,
 *                        no el suyo propio: aplicarle "solo los tuyos" le
 *                        mostraría el contrato de interventoría y le ocultaría
 *                        justo lo que tiene que revisar.
 */

import type { ContratoMarco, So, Usuario } from "@app-types/entities";
import type { Role } from "@constants/roles";
import { mismaEmpresa } from "@/lib/empresa";

/** Roles del contratista: ven únicamente lo suyo. */
export function esRolContratista(rol: Role | null | undefined): boolean {
  return rol === "PLANNER" || rol === "ADOR";
}

export interface ContextoAlcance {
  rol: Role | null | undefined;
  /**
   * Razón social del usuario (`Usuario.empresa`). Es la llave del cruce.
   *
   * NO se usa `contratistaId`: en producción no cruza. Los usuarios traen
   * `"soforesta"` y `"beoriente"` mientras las OT traen `"SOFORESTA"` y
   * `"bobinados-electro-oriente-s.a.s"`, y los de Consorcio Pendare no lo
   * traen del todo. El PMIS ya resolvía esto con `mismaEmpresa`, que se
   * declara «sola fuente de verdad» para el join usuario ↔ contratista y
   * tolera mayúsculas, espacios y el punto final de "S.A.S".
   */
  empresa?: string | undefined;
  email: string;
}

/**
 * Contratos que el usuario puede ver.
 *
 * `sos` solo se usa para el INT — de ahí salen las OT que revisa y, por ellas,
 * los contratos. Se pasa siempre para no tener dos firmas distintas.
 */
export function contratosVisibles(
  ctx: ContextoAlcance,
  contratos: ContratoMarco[],
  sos: Pick<So, "contratoMarcoId" | "interventorId">[],
): ContratoMarco[] {
  if (esRolContratista(ctx.rol)) {
    // Sin empresa resuelta no se muestra NADA. Es deliberado: ante la duda,
    // no enseñar contratos ajenos.
    if (!ctx.empresa) return [];
    return contratos.filter((c) => mismaEmpresa(ctx.empresa, c.contratistaNombre));
  }

  if (ctx.rol === "INT") {
    const suyos = new Set(
      sos
        .filter((s) => s.interventorId === ctx.email)
        .map((s) => s.contratoMarcoId)
        .filter((id): id is string => !!id),
    );
    return contratos.filter((c) => suyos.has(c.id));
  }

  return contratos;
}

/**
 * ¿Ve el módulo "Materiales por CM"?
 *
 * Un contratista de OBRA no tiene nada que hacer ahí. Uno de MATERIALES sí,
 * pero solo con lo suyo.
 *
 * Un contrato SIN `tipo` no habilita el módulo: el campo es opcional en el
 * modelo, y ante un dato incompleto es preferible negar que exponer los
 * pedidos de otro proveedor.
 */
export function puedeVerMateriales(
  ctx: ContextoAlcance,
  contratos: ContratoMarco[],
  sos: Pick<So, "contratoMarcoId" | "interventorId">[],
): boolean {
  if (!esRolContratista(ctx.rol)) return true;
  return contratosVisibles(ctx, contratos, sos).some((c) => c.tipo === "MATERIALES");
}

/** Razón social del usuario. Es la llave del cruce con OT y contratos. */
export function empresaDelUsuario(
  email: string,
  usuarios: Pick<Usuario, "email" | "empresa">[],
): string | undefined {
  return usuarios.find((u) => u.email === email)?.empresa;
}

/**
 * OT de un contratista, por razón social.
 *
 * Mismo cruce que los contratos, y por la misma razón: `So.contratistaId` no es
 * el mismo valor que `Usuario.contratistaId`. En producción la OT-0006 trae
 * `"SOFORESTA"` y su contratista `"soforesta"`; la OT-0005 trae
 * `"bobinados-electro-oriente-s.a.s"` y el suyo `"beoriente"`.
 */
export function otsDelContratista<T extends { contratistaNombre?: string }>(
  empresa: string | undefined,
  sos: T[],
): T[] {
  if (!empresa) return [];
  return sos.filter((s) => mismaEmpresa(empresa, s.contratistaNombre));
}
